import {
  actionsKeys,
  formatAmount,
  permissionActions,
} from "@/lib/utils";
import { setCurrentFinanceYear } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetParkDocStatusCountByCommunityId,
  useGetPaymentReport,
  useGetPaymentStat,
  useGetPositionApprovalLimits,
} from "@/services";
import { FileChartColumn, FileInput, FileSearch, FileText, FileX2, Scale } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const usePayment = () => {
  const navigate = useNavigate();
  const [openTabs, setOpenTabs] = useState<string[]>(["invoice"]);
  const dispatch = useDispatch();
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const [paymentCards, setPaymentCards] = useState([
    {
      id: "create",
      title: "Create Payment",
      module: actionsKeys?.PAYMENT?.toLowerCase(),
      link: `${routes.add}`,
      accessKeys: permissionActions.CREATE,
    },
    {
      id: "park_payment",
      count: 0,
      desc: "Payment submit for Posting",
      title: "Submit for Posting",
      icon: FileText,
      link: `park`,
      subtitle: "Parked Documents",
      subtitleColor: "text-yellow-600",
      apiKey: "vpApproved",
      onlyCount: true,
      module: actionsKeys?.PAYMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "summary",
      data: [
        {
          label: "Posted Amount",
          value: 0,
          apiKey: "postedInvoiceAmount",
        },
        { label: "Paid Amount", value: 0, apiKey: "paidInvoiceAmount" },
        { label: "Pending Amount", value: 0, apiKey: "unpaidInvoiceAmount" },
        // { label: "Reversed Amount", value: 0, apiKey: "reversedInvoiceAmount" },
      ],
      title: "Posted Payment",
      link: `post`,
      module: actionsKeys?.PAYMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reports",
      desc: "Payment 360 Report",
      title: "View Reports",
      icon: FileChartColumn,
      link: `${routes.reports}`,
      subtitle: "",
      subtitleColor: "text-green-600",
      apiKey: "",
      hideCount: true,
      module: actionsKeys?.PAYMENT_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reports",
      desc: "Tolerance Report",
      title: "View Reports",
      icon: FileChartColumn,
      link: `${routes.tolerance}`,
      subtitle: "",
      subtitleColor: "text-green-600",
      apiKey: "",
      hideCount: true,
      module: actionsKeys?.TOLERANCE_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "vendor-trial-balance",
      title: "View",
      desc: "Vendor Trial Balance",
      icon: Scale,
      link: `${routes.vendorTrialBalance}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.VENDOR_TRIAL_BALANCE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "report-by-vendor",
      title: "View",
      desc: "Report By Vendor",
      icon: FileChartColumn,
      link: `${routes.reportByVendor}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.VENDOR_BALANCE_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const [userCardList, setUserCardList] = useState<any>([
    {
      title: communityData?.bomPosition?.positionName,
      subTitle: "Position",
      icon: FileSearch,
    },
    {
      title: "",
      subTitle: "",
      icon: FileInput,
    },
    {
      title: "",
      subTitle: "",
      icon: FileInput,
    },
    {
      title: "",
      subTitle: "",
      icon: FileX2,
    },
  ]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: "",
        name: <span className="text-primary">{"Payment"}</span>,
      },
    ],
    []
  );

  const { data: userData, isLoading: isUserDataLoading } =
    useGetPositionApprovalLimits({
      positionId: communityData?.bomPosition?.bomPositionId,
      year: currentFinanceYear,
      queryParams: {
        enabled: !!(
          communityData?.bomPosition?.bomPositionId && currentFinanceYear
        ),
      },
    });

  useEffect(() => {
    if (userData) {
      const updatedList = userCardList.map((card: any) => ({
        ...card,
        ...(card?.apiKey
          ? {
              title:
                card?.apiKey != "positionName"
                  ? formatAmount(userData?.[card.apiKey])
                  : userData?.[card.apiKey] || "",
            }
          : {}),
      }));

      setUserCardList(updatedList);
    }
  }, [userData]);

  const { data: paymentReportData, isLoading: isLoadingReport } =
    useGetPaymentReport({
      communityId: communityData?.communityId,
      data: {
        budgetYear: currentFinanceYear,
        communityId: communityData?.communityId,
        glMasterIds: [],
      },
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  useEffect(() => {
    let budgetCount = 0,
      budgetAmount = 0,
      commitCount = 0,
      commitAmount = 0,
      actualCount = 0,
      actualAmt = 0;
    paymentReportData?.data?.glAccountData?.map((data: any) => {
      if (data.budgetAmount) {
        budgetCount += 1;
        budgetAmount += data?.budgetAmount;
      }
      if (data.committedAmount) {
        commitCount += 1;
        commitAmount += data.committedAmount;
      }
      if (data.actualAmount) {
        actualCount += 1;
        actualAmt += data.actualAmount;
      }
    });

    userCardList[1].title = `# Budget (GL): ${budgetCount}`;
    userCardList[1].subTitle = `Amount: ${formatAmount(budgetAmount)}`;
    userCardList[2].title = `# Committed (Invoice): ${commitCount}`;
    userCardList[2].subTitle = `Amount: ${formatAmount(commitAmount)}`;
    userCardList[3].title = `# Actual (Invoice): ${actualCount}`;
    userCardList[3].subTitle = `Amount: ${formatAmount(actualAmt)}`;
    setUserCardList(userCardList);
  }, [paymentReportData]);

  const onYearChange = (e: any) => {
    dispatch(setCurrentFinanceYear(e));
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs(e);
  };

  const onExpandCollapse = () => {
    if (openTabs?.length) {
      setOpenTabs([]);
    } else {
      setOpenTabs(["payment", "invoice"]);
    }
  };

  const onCreateCardClick = (link: string) => () => {
    if (communityData) {
      navigate(link);
    } else {
      toast.error("Please select community");
    }
  };

  const onCardClick = (link: string) => () => {
    navigate(link);
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}`);
  };

  const { data: invoiceCount, isLoading: isInvoiceCountLoading } =
    useGetParkDocStatusCountByCommunityId({
      communityId: communityData?.communityId,
      year: currentFinanceYear,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });
    
  const { data: paymentStat, isLoading: isPaymentStatLoading } =
    useGetPaymentStat({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  useEffect(() => {
    // if (!paymentStat && !invoiceCount) return;

    const statData = {...(paymentStat as any)?.data, ...(invoiceCount as any)?.data};

    if (!statData) return;

    const updatedCards = paymentCards.map((card: any) => {
      const { id, data: cardData, apiKey } = card;

      if (id === "summary") {
        const total = Object.values(statData).reduce(
          (acc: number, val: any) => {
            const num = parseFloat(val);
            return acc + (isNaN(num) ? 0 : num);
          },
          0
        );

        const updatedData = cardData.map((item: any) => ({
          ...item,
          value:
            item.apiKey === "total"
              ? total
              : formatAmount(statData?.[item.apiKey] ?? 0),
        }));

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        count: card?.onlyCount ? statData?.[apiKey] ?? 0 : formatAmount(statData?.[apiKey] ?? 0),
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setPaymentCards(updatedCards);
  }, [paymentStat, accessPermission, invoiceCount]);

  return {
    communityData,
    currentFinanceYear,
    onYearChange,
    paymentCards,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    onCardClick,
    breadcrumbData,
    onBackClick,
    userCardList,
    isPaymentStatLoading,
    isUserDataLoading,
    isLoadingReport,
    isInvoiceCountLoading
  };
};

export default usePayment;
